import axios from 'axios';

import { DownloadHttpError } from 'services/file-flow.service';

export type YtUploadErrorKind = 'auth' | 'publish' | 'network' | 'download';

export type YtAuthReason = 'missing' | 'expired' | 'invalid';

export interface YtUploadError {
  kind: YtUploadErrorKind;
  message: string;
  reason?: string;
}

const PUBLISH_FALLBACK = 'Failed to upload to YouTube. Please try again.';
const DOWNLOAD_FALLBACK =
  "Couldn't prepare the video for YouTube. Please try again. If it continues to fail, contact support.";

export const isYtUploadError = (value: unknown): value is YtUploadError =>
  typeof value === 'object' && value !== null && 'kind' in value && 'message' in value;

export const ytAuthError = (reason: YtAuthReason): YtUploadError => ({
  kind: 'auth',
  reason,
  message:
    reason === 'missing'
      ? 'YouTube authorization required'
      : reason === 'expired'
      ? 'YouTube authorization expired'
      : 'YouTube authorization invalid',
});

type GoogleApiErrorBody = {
  error?: {
    message?: string;
    errors?: Array<{ reason?: string; message?: string }>;
  };
};

const getGoogleApiReason = (data: GoogleApiErrorBody | undefined) => data?.error?.errors?.[0]?.reason;

const getGoogleApiMessage = (data: GoogleApiErrorBody | undefined) =>
  data?.error?.errors?.[0]?.message || data?.error?.message;

const publishMessageForReason = (reason: string | undefined, fallback?: string) => {
  switch (reason) {
    case 'youtubeSignupRequired':
      return "This Google account doesn't have a YouTube channel. Create one at youtube.com or switch accounts.";
    case 'insufficientPermissions':
      return "Pulse doesn't have permission to upload to YouTube. Reconnect and allow YouTube access.";
    default:
      return fallback || PUBLISH_FALLBACK;
  }
};

export const classifyYoutubeApiError = (error: unknown): YtUploadError => {
  if (!axios.isAxiosError<GoogleApiErrorBody>(error)) {
    return { kind: 'network', message: PUBLISH_FALLBACK };
  }

  const status = error.response?.status;
  const data = error.response?.data;
  const reason = getGoogleApiReason(data);
  const apiMessage = getGoogleApiMessage(data);

  if (status === 401) return ytAuthError('invalid');

  if (status === 403 || status === 400) {
    return {
      kind: 'publish',
      reason,
      message: publishMessageForReason(reason, apiMessage),
    };
  }

  if (!error.response || (status !== undefined && status >= 500)) {
    return { kind: 'network', message: PUBLISH_FALLBACK };
  }

  return { kind: 'publish', reason, message: apiMessage || PUBLISH_FALLBACK };
};

export const classifyDownloadError = (error: unknown): YtUploadError => {
  if (error instanceof DownloadHttpError) {
    if (error.status >= 500) {
      return { kind: 'network', message: PUBLISH_FALLBACK };
    }
    return { kind: 'download', message: DOWNLOAD_FALLBACK };
  }

  if (!axios.isAxiosError(error)) {
    return { kind: 'download', message: DOWNLOAD_FALLBACK };
  }

  const status = error.response?.status;
  if (status !== undefined && status >= 500) {
    return { kind: 'network', message: PUBLISH_FALLBACK };
  }

  // No response covers CORS blocks and transient client blips; browsers hide which.
  return { kind: 'download', message: DOWNLOAD_FALLBACK };
};
